import type { ScreenerQuestionnaire } from '../types/screener';

export const screenerDisplayName = (q: ScreenerQuestionnaire): string =>
  q.name || q.templatename || 'screener';

/**
 * True when every required, answerable question in the screener has an answer.
 *
 * Matches pre-PR accordion validation: skip `required === false`, and skip
 * questions with neither options nor possibleanswers (including free-text /
 * display-only items that are not option-based).
 */
export const screenerHasAllRequiredAnswers = (
  questionnaire: ScreenerQuestionnaire,
  templateId: number | string,
  answers: Record<string, string>,
): boolean => {
  let hasUnanswered = false;
  questionnaire.sections.forEach((section, sectionIndex) => {
    section.questionlist.forEach((q, qIndex) => {
      if (q.required === false) return;
      const hasOptions = q.options && q.options.length > 0;
      const hasPossibleAnswers = q.possibleanswers && q.possibleanswers.length > 0;
      if (!hasOptions && !hasPossibleAnswers) return;
      const key = `${templateId}_${sectionIndex}_${qIndex}`;
      if (!answers[key]) hasUnanswered = true;
    });
  });
  return !hasUnanswered;
};

/**
 * Names of screeners that still need required answers before Continue may advance.
 * Already-submitted screeners are ignored. Empty payload cases are not treated as
 * incomplete here — Continue should only block on missing required answers.
 */
export const getIncompleteScreenerNames = (
  questionnaires: ScreenerQuestionnaire[],
  submittedIds: Set<number | string>,
  answersByScreener: Record<number | string, Record<string, string>>,
): string[] => {
  const incompleteNames: string[] = [];
  questionnaires.forEach((q) => {
    const tid = q.templateid ?? q.questionnaireid;
    if (submittedIds.has(tid)) return;
    const answers = answersByScreener[tid] || {};
    if (!screenerHasAllRequiredAnswers(q, tid, answers)) {
      incompleteNames.push(screenerDisplayName(q));
    }
  });
  return incompleteNames;
};

/**
 * Whether Wellness Continue may advance without submitting screeners.
 * Used when GET screener-questions failed, or there are no questionnaires to fill.
 */
export const shouldAllowContinueWithoutScreeners = (
  screenerLoadFailed: boolean,
  questionnaires: ScreenerQuestionnaire[],
): boolean => screenerLoadFailed || questionnaires.length === 0;
